import { create } from "zustand";
import { InsightDataType, DeepScanResultType, IndicatorStatusType } from "../types";

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface ScanRecord {
    fingerprint: string;
    internetMessageId?: string;
    scannedAt: string; // ISO date string
    insightData?: Partial<InsightDataType>;
    deepScanData?: Partial<DeepScanResultType>;
    indicatorStatus?: IndicatorStatusType;
}

const STORAGE_KEY = "sniff-detect-scan-history";
const ROAMING_KEY = "scanFingerprints";

// ─── localStorage helpers ───────────────────────────────────────────────────────

function loadFromLocalStorage(): Record<string, ScanRecord> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        console.warn("[ScanHistory] Failed to load from localStorage:", e);
        return {};
    }
}

function saveToLocalStorage(history: Record<string, ScanRecord>) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (e) {
        console.warn("[ScanHistory] Failed to save to localStorage:", e);
    }
}

// ─── roamingSettings helpers ────────────────────────────────────────────────────

/** 
 * Save fingerprint to Office.context.roamingSettings (Exchange-backed, 32KB limit).
 * Only stores fingerprint + scannedAt (lightweight) for cross-device detection.
 */
function saveToRoamingSettings(fingerprint: string, scannedAt: string) {
    try {
        const roaming = Office.context.roamingSettings;
        const existing: Record<string, string> = roaming.get(ROAMING_KEY) || {};
        existing[fingerprint] = scannedAt;

        // Prune old entries to stay within 32KB limit (keep last 200)
        const entries = Object.entries(existing);
        if (entries.length > 200) {
            const sorted = entries.sort(([, a], [, b]) => new Date(b).getTime() - new Date(a).getTime());
            const pruned = Object.fromEntries(sorted.slice(0, 200));
            roaming.set(ROAMING_KEY, pruned);
        } else {
            roaming.set(ROAMING_KEY, existing);
        }

        roaming.saveAsync((result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
                console.warn("[ScanHistory] roamingSettings saveAsync failed:", result.error);
            }
        });
    } catch (e) {
        console.warn("[ScanHistory] Failed to save to roamingSettings:", e);
    }
}

/**
 * Check if a fingerprint exists in roamingSettings (cross-device check).
 */
function checkRoamingSettings(fingerprint: string): string | null {
    try {
        const roaming = Office.context.roamingSettings;
        const existing: Record<string, string> = roaming.get(ROAMING_KEY) || {};
        return existing[fingerprint] || null;
    } catch (e) {
        console.warn("[ScanHistory] Failed to read roamingSettings:", e);
        return null;
    }
}

// ─── Zustand Store ──────────────────────────────────────────────────────────────

type ScanHistoryState = {
    scanHistory: Record<string, ScanRecord>;

    /**
     * Check if an email has been scanned before (checks both localStorage and roamingSettings).
     */
    hasScanRecord: (fingerprint: string) => boolean;

    /**
     * Get the full scan record for a fingerprint (localStorage only, has full data).
     */
    getScanRecord: (fingerprint: string) => ScanRecord | undefined;

    /**
     * Get the scannedAt date from either localStorage or roamingSettings.
     * Useful for showing "Already scanned on [date]" even on a different device.
     */
    getScannedAt: (fingerprint: string) => string | null;

    /**
     * Add a new scan record. Persists to both localStorage and roamingSettings.
     */
    addScanRecord: (record: ScanRecord) => void;

    /**
     * Update an existing scan record (e.g., when deep scan completes after initial scan).
     */
    updateScanRecord: (fingerprint: string, updates: Partial<ScanRecord>) => void;

    /**
     * Clear all scan history.
     */
    clearHistory: () => void;
};

export const useScanHistoryStore = create<ScanHistoryState>((set, get) => ({
    // Hydrate from localStorage on init
    scanHistory: loadFromLocalStorage(),

    hasScanRecord: (fingerprint: string) => {
        // Check localStorage first (full data available)
        if (get().scanHistory[fingerprint]) return true;
        // Fallback: check roamingSettings (cross-device)
        return checkRoamingSettings(fingerprint) !== null;
    },

    getScanRecord: (fingerprint: string) => {
        return get().scanHistory[fingerprint];
    },

    getScannedAt: (fingerprint: string) => {
        const localRecord = get().scanHistory[fingerprint];
        if (localRecord) return localRecord.scannedAt;
        return checkRoamingSettings(fingerprint);
    },

    addScanRecord: (record: ScanRecord) => {
        set((state) => {
            const updated = { ...state.scanHistory, [record.fingerprint]: record };
            saveToLocalStorage(updated);
            saveToRoamingSettings(record.fingerprint, record.scannedAt);
            return { scanHistory: updated };
        });
    },

    updateScanRecord: (fingerprint: string, updates: Partial<ScanRecord>) => {
        set((state) => {
            const existing = state.scanHistory[fingerprint];
            if (!existing) return state;
            const updated = {
                ...state.scanHistory,
                [fingerprint]: { ...existing, ...updates },
            };
            saveToLocalStorage(updated);
            return { scanHistory: updated };
        });
    },

    clearHistory: () => {
        set({ scanHistory: {} });
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) { /* ignore */ }
        try {
            Office.context.roamingSettings.remove(ROAMING_KEY);
            Office.context.roamingSettings.saveAsync();
        } catch (e) { /* ignore */ }
    },
}));
